import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { formatRef } from '@/lib/utils/bible-utils';
import { containsPassage } from '@/lib/outline/outline-tree-utils';
import { motion, AnimatePresence } from 'framer-motion';
import { nodeAccentColorFromPercent, calculateChildAbsMidPercent } from '@/lib/utils/color-utils';

interface PositionedNode {
    node: OutlineNode;
    depth: number;
    topPercent: number;
    heightPercent: number;
    absTopPercent: number;
    absHeightPercent: number;
    absBottomPercent: number;
    absMidPercent: number;
    isHighlighted: boolean;
    parentId?: string;
    siblingIndex?: number;
}

interface IciclePreviewLinesProps {
    column1Nodes: PositionedNode[];
    highlightedPassageId?: string;
    isDarkMode?: boolean;
    onNodeClick?: (node: OutlineNode) => void;
}

export function IciclePreviewLines({
    column1Nodes,
    highlightedPassageId,
    isDarkMode,
    onNodeClick
}: IciclePreviewLinesProps) {
    if (column1Nodes.length === 0) {
        return null;
    }

    return (
        <div className="w-3 shrink-0 pr-2 flex flex-col h-full">
            {column1Nodes.map(parentNode => {
                const children = parentNode.node.children || [];
                if (children.length === 0) {
                    // Spacer for nodes without children to maintain alignment
                    return (
                        <div 
                            key={`spacer-${parentNode.node.id}`}
                            className="py-[8px]"
                            style={{
                                flexGrow: parentNode.heightPercent,
                                flexShrink: 0,
                                flexBasis: `max(${parentNode.heightPercent}%, 56px)`,
                                minHeight: '56px'
                            }}
                        />
                    );
                }
                
                // Container for this parent's children preview lines
                return (
                    <div 
                        key={`preview-${parentNode.node.id}`}
                        className="flex flex-col py-[8px]"
                        style={{
                            flexGrow: parentNode.heightPercent,
                            flexShrink: 0,
                            flexBasis: `max(${parentNode.heightPercent}%, 56px)`,
                            minHeight: '56px'
                        }}
                    >
                        <AnimatePresence initial={false}>
                            {children.map((child) => {
                                const isHighlighted = highlightedPassageId ? containsPassage(child, highlightedPassageId) : false;
                                const childVerses = child.numVerses || 0;
                                const parentVerses = parentNode.node.numVerses || 0;
                                const childHeightPercent = parentVerses > 0 ? (childVerses / parentVerses) * 100 : 0;
                                
                                // Compute child's relative position within parent (0-1 fraction)
                                let childRelTop = 0;
                                for (const s of children) {
                                    if (s === child) break;
                                    const sv = s.numVerses || 0;
                                    childRelTop += parentVerses > 0 ? (sv / parentVerses) : 0;
                                }
                                const childRelHeight = childHeightPercent / 100;
                                
                                // Calculate absolute midpoint for color matching with BookRail
                                const childAbsMidPercent = calculateChildAbsMidPercent(
                                    parentNode.absTopPercent,
                                    parentNode.absHeightPercent,
                                    childRelTop,
                                    childRelHeight
                                );
                                
                                return (
                                    <Tooltip key={child.id} delayDuration={250}>
                                        <TooltipTrigger asChild>
                                            <motion.button
                                                layout
                                                layoutId={child.id}
                                                type="button"
                                                className="w-full rounded-md my-[2px]"
                                                style={{
                                                    flexGrow: childHeightPercent,
                                                    flexShrink: 0,
                                                    flexBasis: `${childHeightPercent}%`,
                                                    minHeight: '2px',
                                                backgroundColor: isHighlighted
                                                        ? 'rgba(250,204,21,0.95)'
                                                        : nodeAccentColorFromPercent(childAbsMidPercent, !!isDarkMode),
                                                    border: 'none'
                                                }}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: isHighlighted ? 1 : 0.7 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ 
                                                    layout: { duration: 0.3, ease: "easeInOut" },
                                                    opacity: { duration: 0.15 }
                                                }}
                                                onClick={() => onNodeClick?.(child)}
                                            />
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="max-w-xs">
                                            <div className="text-xs">
                                                <div className="font-semibold">{formatRef(child.ref)}</div>
                                                <div className="opacity-80">{child.title}</div>
                                            </div>
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );
}

